import PowerduckInitializer from '@powerduck/app/powerduck-initializer'; // has to come first in import list
import '@powerduck/common/extensions/temporal-extensions';
import './common/extensions/inviton-temporal-extensions';
import { AjaxProviderCordova } from '@powerduck/common/ajax-xhr';

import { AppHttpProvider } from '@powerduck/common/api-http';
import { initHistory } from '@powerduck/common/history-handler';
import EsModuleImportHelper from '@powerduck/common/utils/esmodule-import-helper';
import { PortalUtils } from '@powerduck/common/utils/utils';
import { FormItemWrapperConfig } from '@powerduck/components/form/form-item-wrapper';
import { AppConfig } from './app-config';

import { LanguageProvider } from './resources/_LanguageProvider';
import BaseResources from './resources/en.json';

import 'bootstrap';
import './app/scss/styles.scss';
import './template/vendors/mdi/css/materialdesignicons.min.css';
import 'animate.css/animate.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'simple-line-icons/css/simple-line-icons.css';
import 'weather-icons/css/weather-icons.css';
import { AppState } from './app-state';
import TemporalUtils from '@powerduck/common/utils/temporal-utils';
import { Temporal } from '@js-temporal/polyfill';
import { Language } from '@powerduck/common/enums/language';
import ObjectPostProcessor from '@powerduck/common/utils/object-post-processor';
import { BootstrapToggleConfig } from '@powerduck/components/bootstrap-toggle';
import { LoginDialog } from './components/login/login-dialog';

// Global window definitions, supported languages, etc
PowerduckInitializer.initFramework({
	supportedLanguages: [
		Language.sk,
		Language.en,
		Language.cs,
		Language.de,
		Language.pl,
		Language.hu,
	],
});

export const initFramework = (afterInit: () => void) => {
	initHistory();

	// Powerduck's base-component calls `globalState.loginModalRootInstance.show()` on
	// unauthorized API responses. Inviton uses the iframe-based LoginDialog instead of
	// a mounted <LoginModal isRootInstance>, so expose a shim that delegates to it.
	(window as any).loginModalRootInstance = {
		show: () => LoginDialog.show(),
	};

	// (window as any).__toCdnUrl = (src: string): string => {
	//     if (AppConfig.isDebugMode) {
	//         return src;
	//     } else {
	//         return 'https://flexi-seasonpass.gopass.travel/' + src;
	//     }
	// }

	if (AppConfig.isDebugMode) {
		AppHttpProvider.enforceDomain = '/api/v1/';
	} else {
		AppHttpProvider.enforceDomain = '/api/v1/';
	}

	if ((window as any).cordova != null) {
		AppHttpProvider.cordovaRequestProvider = new AjaxProviderCordova();
	}

	if (PortalUtils.isIOS()) {
		const viewport = document.querySelector('meta[name=viewport]') as any;
		viewport.setAttribute('content', `${viewport.getAttribute('content')}, maximum-scale=1`);
	}

	AppHttpProvider.ajaxDefaults = {
		blockUi: null as any,
		headers: [
			{
				name: 'Accept-Language',
				value: () => AppState.currentLanguage as string,
			},
		],
		withCredentials: false,
	};

	FormItemWrapperConfig.labelColumnClass = 'col-md-3';
	FormItemWrapperConfig.contentColumnClass = 'col-md-9';

	//if (AppState.user != null) {
	//	AppHttpProvider.bearerToken = AppState.user.Token;
	//}

	(() => {
		const oldFunc = TemporalUtils.isSerializedDate;
		TemporalUtils.isSerializedDate = (str: string): boolean => {
			if (str == null) {
				return false;
			}

			if (oldFunc(str)) {
				return true;
			}

			if ((str as any).year) {
				return false;
			}

			if (str.indexOf('$idt/') == 0) {
				return true;
			}

			return false;
		}
	})();


	TemporalUtils.fromString = (val: string): Temporal.PlainDateTime => {
		if (val == null || val.length == 0) {
			return null;
		}

		if (TemporalUtils.isSerializedDate(val)) {
			if (val.indexOf('$idt/') == 0) {
				return TemporalUtils.fromEpochMs(Number((<string>val).substring(5)));
			}

			return Temporal.PlainDateTime.from(val.split('Z')[0]);
		}

		return null;
	}

	ObjectPostProcessor.postProcessJsObject = <T>(jsObj: T, jsonAdapter?: any): T => {
		const recIterate = function (obj) {
			for (const key in obj) {
				const keyVal = obj[key];
				if (jsonAdapter != null) {
					jsonAdapter(
						keyVal,
						key,
						obj,
					);
				}

				if (Array.isArray(keyVal)) {
					keyVal.forEach((arrItem) => {
						recIterate(arrItem);
					});
				} else if (keyVal != null && typeof keyVal === 'object') {
					//Translate localized string from inviton form to powerduck
					if (keyVal.Slovak != null || keyVal.English != null || keyVal.Czech != null || keyVal.German != null || keyVal.Polish != null || keyVal.Hungarian != null) {
						const newObj: any = {};
						newObj.sk = keyVal.Slovak;
						newObj.en = keyVal.English;
						newObj.cs = keyVal.Czech;
						newObj.de = keyVal.German;
						newObj.pl = keyVal.Polish;
						newObj.hu = keyVal.Hungarian;
						obj[key] = newObj;
					} else {
						recIterate(keyVal);
					}
				} else {
					if (keyVal != null && keyVal.indexOf && TemporalUtils.isSerializedDate(keyVal)) {
						try {
							obj[key] = TemporalUtils.fromString(keyVal);
						} catch (e) {
							obj[key] = keyVal;
						}
					}
				}
			}
		};

		recIterate(jsObj);
		return jsObj;
	};

	LanguageProvider.loadDefaultLanguage(EsModuleImportHelper.getObj(BaseResources));
	LanguageProvider.ensureLanguage().then(() => {
		PowerduckInitializer.initState(AppState.resources, {
			keyPrefix: 'myAppName',
			getBlockerHtml: () => AppConfig.blockerHtml,
			getBlockerSelector: () => AppConfig.blockerSelector,
			getBootstrapOnStyle: () => AppConfig.onBootstrapStyle,
			getCdnPath: () => AppConfig.cdnPath,
			getCurrentLanguage: () => AppState.currentLanguage,
			getCurrentRouteQuery: () => AppState.router.currentRoute,
			getGeonamesUsername: () => null,
			getGoogleMapsApiKey: () => AppConfig.GOOGLE_API_KEY,
			getIntransparentBlockerHtml: () => AppConfig.intransparentBlockerHtml,
			getModalSectionMode: () => AppState.modalSectionMode,
			parseErrorMessage: respText => AppConfig.parseErrorMessage(respText),
			setModalSectionMode: mode => AppState.setModalSectionMode(mode),
		});

		afterInit();
	});
};
